import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { FieldNumberFormat } from 'components/FieldNumberFormat';
import { ModalDialog } from 'components/ModalDialog';
import { TableLoader } from 'components/TableLoader';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { CostGroupEnum, WorkOrderStatusEnum, ProjectMethodologyEnum } from 'gql/graphql';
import type { ChapterEnum, WorkOrderOtherAggregationData } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { useTableCollapse } from 'hooks/useTableCollapse';
import { CreateOther } from 'pages/Project/Common/Contract/OtherCreate';
import { useConstructionTypesListItems } from 'pages/Project/Common/hooks/useConstructionTypesListItems';
import type { AnyObject } from 'types/AnyObject';
import { Checkbox, ChoiceGroup, IconAlert, Informer, Loader, Table, withTooltip } from 'uikit';
import { ExportCSVReport } from 'utils/createCSVReportFile';
import { replacerCost } from 'utils/replacerCost';

import { noun } from 'plural-ru';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useWorkOrderObjects } from '../hooks/useWorkOrderObjects';

import { addWorkOrderOtherCost, getWorkOrderOther } from './addOrderOtherQueries';
import { createFilterChangeHandler, prepareOtherCostData } from './addOtherHelpers';
import { getAddOthersBaseColumns } from './addOthersBaseColumns';
import type { ICheckedObjs, IOthersData, IOtherValues } from './types';
import { CalculatingType } from './types';
import { useAddOtherData } from './useAddOtherData';
import { useAddOtherFilter } from './useAddOtherFilter';

import Styles from './AddOrderOther.module.css';

export const getAdditionalClass = ({ column, row }: any) =>
    [column?.accessor, !row?.nestLevel ? 'groupHeader' : ''].join(' ');

export const getInitialChecked = (data: AnyObject) =>
    data?.reduce((acc: any, item: AnyObject) => {
        item?.rows?.forEach((rowItem: AnyObject) => {
            if (!acc[item.id]) {
                acc[item.id] = {
                    parent: false,
                };
            }
            acc[rowItem.parentId] = {
                ...acc[rowItem.parentId],
                [rowItem.objectId]: false,
            };
        });

        return acc;
    }, {});

const CheckboxWithToolTip = withTooltip({
    mode: 'mouseover',
    // @ts-ignore
})(Checkbox);

export const AddOthersTable = ({
    onConfirm,
    onCancel,
    constructionTypeId,
    chapter,
    workOrderId,
}: {
    onConfirm: () => void;
    onCancel: () => void;
    constructionTypeId: number;
    chapter: ChapterEnum;
    workOrderId: number;
}) => {
    const { id: projectId, orderId } = useParams();
    const { getConstructionTypeName } = useConstructionTypesListItems();

    const { projectData } = useProjectData(projectId);

    const dispatch = useDispatch();
    const isVariablesFull = !!(workOrderId && chapter && constructionTypeId);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const workOrderOthersVariables = useMemo(
        () => ({
            workOrderId,
            constructionTypeId,
            chapter,
        }),
        [chapter, constructionTypeId, workOrderId],
    );

    const choiceItems: { label: string; isIncluded: boolean }[] = useMemo(
        () => [
            { label: 'Да', isIncluded: true },
            {
                label: 'Нет',
                isIncluded: false,
            },
        ],
        [],
    );
    const [isSubmitted, setIsSubmitted] = useState(false);
    const { workOrderObjects, status: workOrderObjStatus } = useWorkOrderObjects(workOrderId);
    const [checkedIds, setCheckedIds] = useState<ICheckedObjs>({});
    const [otherPriceValues, setOtherPriceValues] = useState<IOtherValues>({});
    const [createdOtherId, setCreatedOtherId] = useState<number | null>(null);
    const [isOpenWarningDialog, setIsOpenWarningDialog] = useState<boolean>(false);

    const addWorkOrderOtherCostVariables = useMemo(
        () => ({
            workOrderId,
            otherCostInput: {
                workOrderId,
                constructionTypeId,
                otherPrices: prepareOtherCostData({ checkedObjects: checkedIds, priceValues: otherPriceValues }),
            },
        }),
        [checkedIds, constructionTypeId, otherPriceValues, workOrderId],
    );

    const [
        addWorkOrderOtherCostData,
        addWorkOrderOtherCostStatus,
        addWorkOrderOtherCostErr,
        addWorkOrderOtherCostRequest,
    ] = useRequest({
        requestId: 'addWorkOrderOtherCost',
        query: addWorkOrderOtherCost,
        variables: addWorkOrderOtherCostVariables,
        withEffect: false,
    });
    const [makeAddOrderOtherReset] = useResetStoreData({ requestId: 'addWorkOrderOtherCost' });

    const isAddInProgress = addWorkOrderOtherCostStatus === RequestStatus.FETCHING;

    useEffect(() => {
        if (addWorkOrderOtherCostData?.uerAddWorkOrderOtherCost?.result === true && !isAddInProgress) {
            makeAddOrderOtherReset(undefined);
            onConfirm?.();
        }
    }, [
        addWorkOrderOtherCostData?.uerAddWorkOrderOtherCost?.result,
        isAddInProgress,
        makeAddOrderOtherReset,
        onConfirm,
    ]);

    const [workOrderOthers, workOrderOtherStatus, , workOrderOtherRequest] = useRequest({
        requestId: 'addOthersTableData',
        query: getWorkOrderOther,
        withEffect: isVariablesFull,
        // @ts-ignore
        variables: workOrderOthersVariables,
    });

    const isTableLoading =
        workOrderOtherStatus === RequestStatus.FETCHING || workOrderObjStatus === RequestStatus.FETCHING;
    const isTableLoadingSuccess =
        workOrderOtherStatus === RequestStatus.SUCCESS || workOrderObjStatus === RequestStatus.SUCCESS;

    const btnConfirmDisabled = useMemo(() => !Object.values(checkedIds).some((item) => item.parent), [checkedIds]);

    const tableFilters = useAddOtherFilter(workOrderOthers?.uerWorkOrderOther);
    const [visibleTableData, setVisibleTableData] = useState<AnyObject[]>([]);

    useEffect(() => {
        if (workOrderOthers?.uerWorkOrderOther) {
            setVisibleTableData(workOrderOthers?.uerWorkOrderOther);
        }
    }, [workOrderOthers?.uerWorkOrderOther]);

    const handleFilterChange = useMemo(
        () => createFilterChangeHandler(workOrderOthers?.uerWorkOrderOther, setVisibleTableData),
        [workOrderOthers?.uerWorkOrderOther],
    );

    const groupedData = useAddOtherData({
        workOrderOthers: visibleTableData as Partial<WorkOrderOtherAggregationData>[],
        workOrderObjects: workOrderObjects ?? [],
        constructionTypeId,
    });

    const addOthersBaseColumns = useMemo(
        () =>
            getAddOthersBaseColumns(
                projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0,
            ),
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding],
    );

    const [preparedColumns, preparedRows, , collapseAll] = useTableCollapse(
        'add_others',
        addOthersBaseColumns,
        groupedData,
    );

    useEffect(() => {
        if (preparedRows.length && !Object.keys(checkedIds).length) {
            const initialChecked = getInitialChecked(groupedData);

            setCheckedIds(initialChecked);
            collapseAll()();
        }
    }, [checkedIds, collapseAll, groupedData, preparedRows, workOrderOtherStatus]);

    useEffect(() => () => setCheckedIds({}), []);

    const resetOtherSeasons = useCallback(
        (row) =>
            row?.limits?.reduce((acc: Record<number, AnyObject>, limit: { season: { id: number } }) => {
                if (limit?.season?.id) {
                    // eslint-disable-next-line prefer-destructuring
                    acc[limit.season.id] = choiceItems[1];
                }

                return acc;
            }, {}),
        [choiceItems],
    );

    const handleChangeCheckbox = useCallback(
        (row: IOthersData) =>
            ({ checked }: { checked: boolean }) => {
                if (!row.parentId) {
                    const allChildItemsChecked = groupedData
                        ?.find((item) => item?.id === row.id)
                        ?.rows?.reduce((acc: Record<number, boolean>, child) => {
                            acc[child.objectId] = checked;

                            return acc;
                        }, {});

                    if (row.limits && checked) {
                        const defaultSeasonPriceValues = resetOtherSeasons(row);

                        setOtherPriceValues((prev) => ({
                            ...prev,
                            [row.id]: defaultSeasonPriceValues || {},
                        }));
                    }

                    setCheckedIds((ids) => ({
                        ...ids,
                        [row.id]: {
                            parent: checked,
                            ...allChildItemsChecked,
                        },
                    }));
                } else {
                    setCheckedIds((ids) => {
                        const childCount = Object.entries(ids[row.parentId] || {}).length - 1;
                        const prevCheckedCount = Object.entries(ids[row.parentId] || {})?.filter(
                            ([id, idValue]) => id !== 'parent' && idValue,
                        ).length;
                        const checkedCount = checked ? prevCheckedCount + 1 : prevCheckedCount - 1;
                        const countsEqual = checkedCount === childCount;
                        const parentState = countsEqual || checkedCount === 0 ? checked : 'intermediate';

                        if (countsEqual) {
                            const defaultSeasonPriceValues = resetOtherSeasons(
                                groupedData.find((dataRow) => dataRow.contractOtherCostId === Number(row.parentId)),
                            );

                            setOtherPriceValues((prev) => ({
                                ...prev,
                                [row.parentId]: defaultSeasonPriceValues || {},
                            }));
                        }

                        return {
                            ...ids,
                            [row.parentId]: {
                                ...ids?.[row.parentId],
                                parent: parentState,
                                [row.objectId]: checked,
                            },
                        };
                    });
                }
            },

        [groupedData, resetOtherSeasons],
    );

    useEffect(() => {
        if (isTableLoadingSuccess && createdOtherId) {
            const row = preparedRows.find((d) => d.id === createdOtherId);

            if (row) {
                handleChangeCheckbox(row)({ checked: true });
                setCreatedOtherId(null);
            }
        }
    }, [createdOtherId, groupedData, handleChangeCheckbox, isTableLoadingSuccess, preparedRows]);

    const handleChangeValues = useCallback(
        (otherId: number | string, seasonId: number, isNumberField = false) =>
            ({ value }: any) => {
                if (!value || value === '-') {
                    return;
                }

                const valueForSave = !isNumberField ? { ...value } : { includedCost: value };

                setOtherPriceValues((ids) => ({
                    ...ids,
                    [otherId]: {
                        ...ids?.[otherId],
                        [seasonId]: {
                            ...valueForSave,
                        },
                    },
                }));
            },
        [],
    );

    const finalColumns = useMemo(
        () => [
            {
                title: '',
                accessor: 'checkbox',
                width: 40,
                withoutPadding: true,
                renderCell: (row: IOthersData & { nestLevel: number }) => {
                    const checkedObjsCounter =
                        checkedIds?.[row.id] &&
                        Object.entries(checkedIds[row.id]).reduce(
                            (acc, [id, value]) => {
                                if (id !== 'parent') {
                                    acc.totalCount += 1;
                                    if (value) {
                                        acc.checkedCount += 1;
                                    }
                                }

                                return acc;
                            },
                            { checkedCount: 0, totalCount: 0 },
                        );

                    return (
                        <div style={{ margin: '16px 12px' }}>
                            {row.nestLevel === 0 ? (
                                <CheckboxWithToolTip
                                    // @ts-ignore
                                    checked={!!checkedIds?.[row?.id]?.parent}
                                    onChange={handleChangeCheckbox(row)}
                                    intermediate={checkedIds?.[row.id]?.parent === 'intermediate'}
                                    tooltipProps={{
                                        content: `${checkedObjsCounter?.checkedCount}/${checkedObjsCounter?.totalCount}
                                        ${noun(checkedObjsCounter?.totalCount, ...['объекта', 'объектов'])}`,
                                        className: Styles.tooltip,
                                    }}
                                />
                            ) : (
                                <Checkbox
                                    checked={checkedIds?.[row?.parentId]?.[row?.objectId]}
                                    onChange={handleChangeCheckbox(row)}
                                />
                            )}
                        </div>
                    );
                },
            },
            ...preparedColumns,
            {
                title: 'Включить в НЗ',
                accessor: 'addToWorkOrder',
                width: 50,

                renderCell: (row: IOthersData) => (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
                        {row?.limits?.map((limit) => {
                            const isDisabled = !checkedIds[row.id]?.parent;

                            const currentId = `${row.id}_${limit.season.id}`;
                            const currentSeasonRemainderCost = limit?.totalRemainderCost;

                            if (row.calculatingType === CalculatingType.CALCULATION) {
                                const inputValue = otherPriceValues?.[row.id]?.[limit.season.id]?.includedCost;
                                const isDeviated = replacerCost(inputValue) > currentSeasonRemainderCost;

                                return (
                                    <FieldNumberFormat
                                        key={currentId}
                                        value={inputValue}
                                        decimalScale={
                                            projectData?.contractNavigation?.data?.contractNavData?.metadata
                                                ?.costRounding ?? 0
                                        }
                                        size="xs"
                                        onChange={handleChangeValues(row.id, limit.season.id, true)}
                                        disabled={isDisabled}
                                        leftSide={isDeviated ? '▲' : undefined}
                                        className={Styles.editableField}
                                    />
                                );
                            }
                            if (row.calculatingType === CalculatingType.PERCENT) {
                                return (
                                    <ChoiceGroup
                                        key={currentId}
                                        value={otherPriceValues?.[row.id]?.[limit.season.id] ?? choiceItems[1]}
                                        onChange={handleChangeValues(row.id, limit.season.id)}
                                        items={choiceItems}
                                        getItemLabel={({ label }) => label}
                                        multiple={false}
                                        name="addToWorkOrder"
                                        size="xs"
                                        style={{ marginBottom: 10 }}
                                        disabled={isDisabled}
                                    />
                                );
                            }

                            return null;
                        })}
                    </div>
                ),
            },
        ],
        [
            preparedColumns,
            checkedIds,
            handleChangeCheckbox,
            otherPriceValues,
            projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding,
            handleChangeValues,
            choiceItems,
        ],
    );

    const handleSave = useCallback(() => {
        dispatch(addWorkOrderOtherCostRequest(addWorkOrderOtherCostVariables));
        setIsSubmitted(true);

        sendGpnAnalyticsEvent({
            componentId: 'rim_NZ_add to order other button_pressed',
            projectId,
            orderId: Number(orderId),
            addType: CostGroupEnum.Others,
            orderStatus: WorkOrderStatusEnum.OnFormation,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        });
    }, [
        addWorkOrderOtherCostRequest,
        addWorkOrderOtherCostVariables,
        dispatch,
        orderId,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
    ]);

    const handleCancel = useCallback(() => {
        const haveCheckedOthers = Object.values(checkedIds).some((checkedId) => checkedId.parent);

        if (haveCheckedOthers) {
            setIsOpenWarningDialog(true);
        } else {
            onCancel();
        }
    }, [checkedIds, onCancel]);

    const handleCloseExactly = useCallback(() => {
        onCancel();
    }, [onCancel]);

    const handleCreateReport = useCallback(() => {
        ExportCSVReport(addWorkOrderOtherCostData?.uerAddWorkOrderOtherCost?.validation);
    }, [addWorkOrderOtherCostData?.uerAddWorkOrderOtherCost?.validation]);

    const handleCloseModalError = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const handleCancelWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
    }, []);

    const reloadWorkOrderOtherTable = useCallback(
        (id: number) => {
            setCreatedOtherId(id);
            dispatch(workOrderOtherRequest(workOrderOthersVariables));
        },
        [dispatch, workOrderOtherRequest, workOrderOthersVariables],
    );

    return (
        <ModalDialog
            title={`Лимиты прочих затрат для вида строительства "${getConstructionTypeName(constructionTypeId)}"`}
            isOpen
            onConfirm={handleSave}
            onClose={handleCancel}
            btnConfirmLabel="Добавить в Наряд-Заказ"
            withTitleCloseButton
            leftPanel={
                <CreateOther
                    currentConstructionTypeId={constructionTypeId}
                    reloadHandler={reloadWorkOrderOtherTable}
                    methodology={ProjectMethodologyEnum.Uer}
                    workOrderId={workOrderId}
                />
            }
            style={{ maxWidth: 1140 }}
            isCommit={isAddInProgress}
            btnConfirmDisabled={btnConfirmDisabled}
            isOnClickOutsideDisable
        >
            <div style={{ margin: '20px' }}>
                <Informer
                    status="system"
                    label="Выбранные прочие затраты по умолчанию будут применены ко всем объектам данного вида строительства. Для того, чтобы исключить объекты, нажмите кнопку раскрытия и снимите выделение."
                    icon={IconAlert}
                />
            </div>
            <div className={Styles.otherTable}>
                {isTableLoading && <Loader />}

                {!isTableLoading && (
                    <>
                        <Table
                            columns={finalColumns}
                            rows={preparedRows}
                            size="s"
                            className={`${Styles.otherTable} workOrderPrice`}
                            stickyHeader
                            emptyRowsPlaceholder={<TableLoader requestStatus={workOrderOtherStatus} />}
                            getAdditionalClassName={getAdditionalClass}
                            verticalAlign="center"
                            filters={tableFilters}
                            onFiltersUpdated={handleFilterChange}
                        />
                        {isOpenWarningDialog && (
                            <ConfirmationDialog
                                textHeader=""
                                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                                cancelButtonText="Не отменять"
                                confirmButtonText="Отменить"
                                isOpened
                                onSubmit={handleCloseExactly}
                                onClose={handleCancelWarningDialog}
                            />
                        )}
                    </>
                )}
            </div>
            <ErrorHandlingModal
                titleText="Добавить прочие"
                isSubmitted={isSubmitted}
                statusRequest={addWorkOrderOtherCostStatus}
                dataRequest={addWorkOrderOtherCostData?.uerAddWorkOrderOtherCost}
                errorsRequest={addWorkOrderOtherCostErr}
                handleCloseModalError={handleCloseModalError}
                handleCreateReport={handleCreateReport}
            />
        </ModalDialog>
    );
};

AddOthersTable.displayName = 'AddOthersTable';
